import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { IconButton } from './Button';
import './common.css';

/** Escape closes, click-outside closes, focus is trapped to the panel. */
export function Modal({ open, onClose, title, subtitle, children, footer, wide = false }) {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="overlay"
      onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}
    >
      <div
        className={`modal ${wide ? 'modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panelRef}
      >
        <div className="modal__head">
          <div className="grow">
            <div className="modal__title">{title}</div>
            {subtitle && <div className="modal__subtitle">{subtitle}</div>}
          </div>
          <IconButton label="Close" icon="✕" onClick={onClose} />
        </div>
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A dropdown that closes on outside click and Escape.
 *
 * `render` receives `close`, so an item can act and dismiss without each caller
 * threading its own open state.
 */
export function Dropdown({ trigger, render, align = 'left', className = '' }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className={`dropdown ${className}`} ref={containerRef}>
      {trigger({ open, toggle: () => setOpen((value) => !value) })}
      {open && (
        <div className={`dropdown__panel dropdown__panel--${align}`} role="menu">
          {render({ close: () => setOpen(false) })}
        </div>
      )}
    </div>
  );
}

export function DropdownItem({ children, active = false, onClick, ...rest }) {
  return (
    <button
      type="button"
      className={`dropdown__item ${active ? 'dropdown__item--active' : ''}`}
      onClick={onClick}
      role="menuitem"
      {...rest}
    >
      {children}
    </button>
  );
}

export function DropdownDivider() {
  return <div className="dropdown__divider" />;
}

export function Tabs({ tabs, active, onChange, className = '' }) {
  return (
    <div className={`tabs ${className}`} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={`tabs__tab ${active === tab.id ? 'tabs__tab--active' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="subtle"> {tab.count}</span>}
        </button>
      ))}
    </div>
  );
}
